/**
 * P1a · Six primary hubs — Find / Market / Connect / Me.
 *
 * These pages exist to (1) satisfy the new Section 6 primary nav and
 * (2) act as landing surfaces that route the user to the correct
 * existing module. They do NOT duplicate module logic — each hub is a
 * short grid + a search box that farms out to the existing routes.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Search, MapPin, Store, MessageCircle, User, Compass, Sparkles,
  Car, Building2, Plane, Video, Utensils, HeartPulse, Briefcase, Heart,
  Star, ShoppingBag, Coins, Wallet, Lock, Bell, Ticket, Newspaper, Users,
  Package, Cpu, Landmark, GraduationCap, Flame, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AinaConfirmSheet from "@/components/AinaConfirmSheet";
import AppIcon, { canonicalIconName } from "@/components/AppIcon";

const CARD = "block rounded-2xl border border-stone-200 bg-white hover:border-stone-300 hover:shadow-md transition-all p-4 flex flex-col items-center text-center gap-2";

/** Hub tile — prefers a Find2near 3D icon when available; falls back to the
 * supplied Lucide component so dense hubs never lose iconography. */
function HubTile({ testId, to, label, iconHint, Icon }) {
  const canonical = canonicalIconName(iconHint || label);
  return (
    <Link to={to} className={CARD} data-testid={testId}>
      {canonical ? (
        <AppIcon name={canonical} size={44} alt={label} />
      ) : (
        <Icon size={22} className="text-emerald-700"/>
      )}
      <div className="text-sm font-bold text-stone-800">{label}</div>
    </Link>
  );
}

/* ── 1. FIND ─────────────────────────────────────────────────────────── */
export function FindHub() {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [decision, setDecision] = useState(null);
  const nav = useNavigate();
  const submit = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/aina/route", { query: q.trim() });
      if (data.needs_confirmation) {
        setDecision(data);
      } else {
        nav(data.redirect_url);
      }
    } catch (e) { toast.error("AINA is busy — please retry"); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-3xl mx-auto p-4" data-testid="hub-find">
      <div className="text-3xl font-black text-stone-900">Find</div>
      <div className="text-sm text-stone-500 mb-4">Tell us what you need — AINA takes you to the right place.</div>
      <div className="flex gap-2">
        <input value={q} onChange={e => setQ(e.target.value)}
               onKeyDown={e => e.key === "Enter" && submit()}
               placeholder="Plumber, veg restaurant, cab to airport…"
               className="flex-1 rounded-full border border-stone-300 px-4 py-3 outline-none focus:border-emerald-500"
               data-testid="find-hub-input"/>
        <button onClick={submit} disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-5 font-bold disabled:opacity-60"
                data-testid="find-hub-go">
          {busy ? "…" : <Sparkles size={18}/>}
        </button>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-6">
        {[
          ["Cab",        "/services/cab",  Car,        "cabs"],
          ["Restaurants","/food",          Utensils,   "marketplace"],
          ["Hotels",     "/services/hotel",Building2,  "hotels"],
          ["Flights",    "/flights",       Plane,      "flights"],
          ["Movies",     "/movies",        Video,      "movies"],
          ["Doctors",    "/health",        HeartPulse, "health"],
          ["Jobs",       "/jobs",          Briefcase,  "jobs"],
          ["Charity",    "/charity",       Heart,      "charity"],
          ["Astrology",  "/astrology",     Star,       "astrology"],
          ["Property",   "/property",      Building2,  "property"],
          ["Trains",     "/live-trains",   MapPin,     "trains"],
          ["Washrooms",  "/washrooms",     MapPin,     null],
        ].map(([label, to, Icon, hint]) => (
          <HubTile key={to} testId={`find-tile-${label.toLowerCase().replace(/\s/g,"-")}`}
                   to={to} label={label} Icon={Icon} iconHint={hint}/>
        ))}
      </div>
      {decision && (
        <AinaConfirmSheet
          decision={decision}
          onCancel={() => setDecision(null)}
          onConfirm={() => { const url = decision.redirect_url; setDecision(null); nav(url); }}
        />
      )}
    </div>
  );
}

/* ── 2. MARKET ───────────────────────────────────────────────────────── */
export function MarketHub() {
  return (
    <div className="max-w-3xl mx-auto p-4" data-testid="hub-market">
      <div className="text-3xl font-black text-stone-900">Market</div>
      <div className="text-sm text-stone-500 mb-4">Products · Services · Bookings — everything transactional in one place.</div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {[
          ["Shop",       "/products",      ShoppingBag, "marketplace"],
          ["Cart",       "/cart",          ShoppingBag, "cart"],
          ["Orders",     "/orders",        Package,     "delivery"],
          ["Cab",        "/services/cab",  Car,         "cabs"],
          ["RideShare",  "/rideshare",     Car,         "rideshare"],
          ["Hotels",     "/services/hotel",Building2,   "hotels"],
          ["Flights",    "/flights",       Plane,       "flights"],
          ["Movies",     "/movies",        Video,       "movies"],
          ["Food",       "/food",          Utensils,    "marketplace"],
          ["Services",   "/services",      Briefcase,   "jobs"],
          ["Property",   "/property",      Building2,   "property"],
          ["Bills",      "/bills",         Coins,       "recharges"],
          ["Charity",    "/charity",       Heart,       "charity"],
          ["Astrology",  "/astrology",     Star,        "astrology"],
          ["Visa",       "/visa",          Landmark,    null],
        ].map(([label, to, Icon, hint]) => (
          <HubTile key={to} testId={`market-tile-${label.toLowerCase().replace(/\s/g,"-")}`}
                   to={to} label={label} Icon={Icon} iconHint={hint}/>
        ))}
      </div>
    </div>
  );
}

/* ── 3. CONNECT ──────────────────────────────────────────────────────── */
export function ConnectHub() {
  return (
    <div className="max-w-3xl mx-auto p-4" data-testid="hub-connect">
      <div className="text-3xl font-black text-stone-900">Connect</div>
      <div className="text-sm text-stone-500 mb-4">Messages, calls, feeds and communities.</div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {[
          ["Messenger",  "/messenger",  MessageCircle, "messenger"],
          ["Notifications","/me/notifications", Bell,  "notifications"],
          ["Feed",       "/feed",       Newspaper,    "social"],
          ["Reels",      "/reels",      Video,        null],
          ["Community",  "/community",  Users,        "social"],
          ["Matches",    "/matrimonial",Users,        "matrimonial"],
          ["Dating",     "/dating",     Users,        "matrimonial"],
          ["Contacts",   "/contacts",   Users,        null],
          ["Meet",       "/meet",       Video,        null],
        ].map(([label, to, Icon, hint]) => (
          <HubTile key={to} testId={`connect-tile-${label.toLowerCase().replace(/\s/g,"-")}`}
                   to={to} label={label} Icon={Icon} iconHint={hint}/>
        ))}
      </div>
    </div>
  );
}

/* ── 4. ME ───────────────────────────────────────────────────────────── */
export function MeHub() {
  return (
    <div className="max-w-3xl mx-auto p-4" data-testid="hub-me">
      <div className="text-3xl font-black text-stone-900">Me</div>
      <div className="text-sm text-stone-500 mb-4">Your profile, wallet, documents and control center.</div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {[
          ["Profile",    "/account",    User,          "me"],
          ["Dashboard",  "/dashboard",  LayoutIcon(),  null],
          ["Wallet",     "/wallet",     Wallet,        "wallet"],
          ["FindPay",    "/findpay",    Coins,         "findpay"],
          ["Locker",     "/locker",     Lock,          "locker"],
          ["Bookings",   "/dashboard",  Ticket,        null],
          ["Notifications","/me/notifications",Bell,   "notifications"],
          ["Devices",    "/me/security/devices",ShieldCheck, null],
          ["Priority",   "/priority-card",Ticket,      null],
          ["Settings",   "/settings/language",Cpu,     null],
          ["Life",       "/life",       Sparkles,      null],
          ["Health",     "/health",     HeartPulse,    "health"],
          ["Emergency",  "/emergency",  Flame,         "emergency"],
        ].map(([label, to, Icon, hint]) => (
          <HubTile key={to} testId={`me-tile-${label.toLowerCase().replace(/\s/g,"-")}`}
                   to={to} label={label} Icon={Icon} iconHint={hint}/>
        ))}
      </div>
    </div>
  );
}

// Tiny inline helper so the map layout icon works without another import.
function LayoutIcon() {
  return function LI({ size = 22, className = "" }) {
    return <Compass size={size} className={className}/>;
  };
}

/* ── Legacy redirects (kept compat per user directive) ───────────────── */
export function Redirect({ to }) { return <Navigate to={to} replace/>; }
